import { Button, Flex, Heading, Link, Text } from "@chakra-ui/react";
import _ from "lodash";
import React, { useCallback, useMemo } from "react";
import { RiShareBoxLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { FormikSwitchableContent } from "src/components/Inputs/FormikSwitchableContent";
import { useNavigationBlockerContext } from "src/components/NavigationBlocker/context";
import * as OrgConfig from "src/generated/hooks/org-config/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useOrganization } from "src/hooks/useOrganization";
import * as OrgService from "src/services/orgService";
import * as Url from "src/services/url";
import * as RD from "src/types/remoteData";
import { z } from "zod";
import { DefaultConfig } from "./Formik";

export const Form: React.FC = () => {
  const organization = useOrganization();
  const navigate = useNavigate();
  const toast = useAvelaToast();
  const navBlocker = useNavigationBlockerContext();
  const editLandingPageClickHandler = useCallback(async () => {
    if (!organization.hasData()) {
      console.error(
        "Clicking edit landing page button without organization data"
      );
      return;
    }

    try {
      await navBlocker.disable(true);
      navigate(Url.OrgAdmin.Org.editLandingPage(organization.data));
    } catch (err) {
      console.error(err);
      toast.genericError();
    }
  }, [organization, navBlocker, navigate, toast]);

  return (
    <Flex direction="column" gap={2}>
      <Heading as="h3" variant="admin">
        Landing page and login methods
      </Heading>
      <FormikSwitchableContent<"enabled" | "disabled">
        name="landingPage.status"
        label="Landing page"
        description={
          <Text>
            Enables a landing page to guide family and staff login.{" "}
            <Link
              color="blue.500"
              textDecoration="underline"
              href="https://help.avela.org/hc/en-us/articles/48399543345805-Landing-Page"
              isExternal
            >
              Learn more
            </Link>
          </Text>
        }
        checked={["enabled", ""]}
        unchecked={["disabled", ""]}
        showContent={(value): boolean => value === "enabled"}
      >
        <Button
          variant="outline"
          colorScheme="gray"
          rightIcon={<RiShareBoxLine />}
          alignSelf="flex-start"
          onClick={editLandingPageClickHandler}
        >
          Set up landing page
        </Button>
      </FormikSwitchableContent>
    </Flex>
  );
};

export function useService(): {
  initialValues: RD.RemoteData<Error, FormikType>;
  submit: (values: FormikType) => Promise<void>;
} {
  const toast = useAvelaToast();
  const { remoteData, update } =
    OrgService.useOrgConfig<OrgConfig.LandingPageConfig>("LandingPage");

  const initialValues = useMemo(() => {
    return remoteData.map((config): FormikType => {
      if (config?.disabled === false) {
        return { landingPage: { status: "enabled" } };
      }

      return {
        landingPage: {
          status: "disabled",
        },
      };
    });
  }, [remoteData]);

  const submit = useCallback(
    async (values: FormikType) => {
      if (!remoteData.hasData() || !initialValues.hasData()) {
        console.warn("landing page config is not loaded yet");
        toast.genericError();
        return;
      }

      if (
        !_.isEqual(
          initialValues.data.landingPage.status,
          values.landingPage.status
        )
      ) {
        const config = {
          ...DefaultConfig,
          ...remoteData.data,
          disabled: values.landingPage.status === "disabled",
        };
        await update(config);
      }
    },
    [initialValues, remoteData, toast, update]
  );

  return { initialValues, submit };
}

export const FormikSchema = z.object({
  landingPage: z.object({
    status: z.union([z.literal("enabled"), z.literal("disabled")]),
  }),
});

export type FormikType = z.infer<typeof FormikSchema>;

export { Edit } from "./Edit";
